import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Eyebrow } from "./Shared";
import { site } from "@/site.config";

const cards = [
  {
    num: "01",
    title: "u-topia Super App",
    tag: "Money that moves with you.",
    body: "The super app: a wallet you control, a card you can spend, rails that settle at digital speed. Send, hold, and pay like a modern bank, without giving the system away. Built for people who live across borders and are done waiting on yesterday's plumbing.",
  },
  {
    num: "02",
    title: "U.CENTER",
    tag: "The community of stakeholders.",
    body: "Learn the system, then help run it. Education, membership, and a global network of people building u-topia in their own cities. Not customers at the edge of someone else's product. Stakeholders in a shared financial future. Live from May 2026.",
  },
  {
    num: "03",
    title: "Filling Rooms",
    tag: "The movement.",
    body: "A 22-city world tour put the vision in people's hands and ignited a community of stakeholders around it. Rooms of twenty. Rooms of two hundred. Then the Bangkok Summit, 15-16 October 2026, and the public super-app launch on the 16th. This is not grown from a studio. It is grown by the people.",
  },
];

const subLinks = [
  { label: "u-topia.com", href: site.links.utopia },
  { label: "u.center", href: site.links.ucenter },
];

export default function Building() {
  return (
    <section id="building" data-testid="building-section" className="scroll-mt-20 bg-navy py-24 lg:py-32">
      <div className="mx-auto w-full max-w-[1200px] px-6">
        <Reveal>
          <Eyebrow>The work</Eyebrow>
          <h2 className="mt-4 max-w-3xl text-3xl font-semibold leading-[1.1] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
            A super app. A school. A community that{" "}
            <span className="text-brand-orange">owns the upside.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            u-topia puts YOU first: modern banking, digital assets, and real-world payments in
            one system. U.CENTER is the learning home. Together they are not a slogan. They are
            a design: use the product, learn the system, share in what you help grow.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1}>
              <article
                data-testid={`building-card-${c.num}`}
                className="flex h-full flex-col rounded-2xl border border-white/10 bg-navy-lift p-8"
              >
                <span className="text-sm font-semibold tracking-[0.2em] text-brand-orange">
                  {c.num}
                </span>
                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-brand-orange">
                  {c.title}
                </h3>
                <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-white/60">
                  {c.tag}
                </p>
                <p className="mt-5 text-[15px] leading-relaxed text-white/80">{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-8">
            {subLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`building-link-${l.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-orange underline decoration-brand-orange/40 underline-offset-4 transition-colors duration-200 hover:text-brand-orange-light"
              >
                {l.label}
                <ArrowUpRight size={14} />
              </a>
            ))}
          </div>
        </Reveal>

        {/* Optional metrics strip - enable only when verified figures are supplied:
        <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label}>
              <p className="text-3xl font-semibold text-brand-orange">{m.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/50">{m.label}</p>
            </div>
          ))}
        </div> */}
      </div>
    </section>
  );
}
